import { useState, useRef, useEffect } from 'react';
import { useSceneStore } from '../../stores/sceneStore';
import { GLTFLoader } from 'three-stdlib';
import { hasValidSkeleton } from './utils/modelValidation';

/**
 * Props for the ModelLoader component
 */
interface ModelLoaderProps {
  /**
   * The ID of the tab to load the model for
   */
  tabId: string;
}

/**
 * ModelLoader Component
 *
 * Provides a file picker to load GLTF/GLB models for a specific tab.
 * Validates that selected models have a valid skeleton structure for animation.
 *
 * Features:
 * - File input accepting .gltf and .glb files
 * - Model validation (checks for skeleton)
 * - Per-tab model storage via sceneStore
 * - Error handling and user feedback
 * - Object URL cleanup on unmount
 *
 * @example
 * ```tsx
 * <ModelLoader tabId="tab-123" />
 * ```
 */
export function ModelLoader({ tabId }: ModelLoaderProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const setModelPath = useSceneStore((state) => state.setModelPath);

  // Cleanup object URL on unmount or when it changes
  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  /**
   * Validates a model loaded from an object URL
   */
  const validateModel = async (url: string): Promise<boolean> => {
    try {
      // Load the GLTF model using GLTFLoader
      const loader = new GLTFLoader();
      const gltf = await loader.loadAsync(url);

      // Validate skeleton structure
      if (!hasValidSkeleton(gltf)) {
        setError(
          'Invalid model: No skeleton found. Please load a model with a rigged skeleton.'
        );
        return false;
      }

      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load model';
      setError(`Model loading failed: ${errorMessage}`);
      return false;
    }
  };

  /**
   * Handles file selection from the file input
   */
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    // Reset state
    setError(null);
    setSelectedFile(null);

    // Revoke previous object URL if it exists
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }

    if (!file) {
      return;
    }

    // Validate file type
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== 'gltf' && fileExtension !== 'glb') {
      setError('Invalid file type. Please select a .gltf or .glb file.');
      return;
    }

    setSelectedFile(file);
    setIsValidating(true);

    // Create object URL for the file
    const url = URL.createObjectURL(file);
    setObjectUrl(url);

    // Validate the model
    const isValid = await validateModel(url);

    setIsValidating(false);

    if (isValid) {
      // Update sceneStore with the model path
      setModelPath(tabId, url);
      setError(null);
    } else {
      // Clean up invalid model
      URL.revokeObjectURL(url);
      setObjectUrl(null);
      setSelectedFile(null);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <label
          htmlFor={`model-input-${tabId}`}
          className="cursor-pointer rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2"
        >
          Load Model
          <input
            ref={fileInputRef}
            id={`model-input-${tabId}`}
            type="file"
            accept=".gltf,.glb"
            onChange={handleFileChange}
            className="sr-only"
            aria-label="Load 3D model file"
          />
        </label>

        {selectedFile && (
          <span className="text-sm text-gray-300">{selectedFile.name}</span>
        )}

        {isValidating && (
          <span className="text-sm text-blue-400">Validating...</span>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-md border border-red-500 bg-red-950/90 p-3 text-sm text-red-200">
          <p className="font-semibold text-red-100">Error</p>
          <p className="mt-1">{error}</p>
        </div>
      )}
    </div>
  );
}
